import { useState } from "react";
import { toast } from "sonner";
import { XCircle, Loader2 } from "lucide-react";
import { authApi, formatApiErrorDetail } from "@/lib/auth";

const fmtLocal = (iso) =>
  new Date(iso).toLocaleString("en-GB", { timeZone: "Europe/London", weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const StatusPill = ({ status }) => (
  <span className={`border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] ${status === "confirmed" ? "border-gold/60 text-gold" : "border-slate-600 text-slate-500"}`}>
    {status}
  </span>
);

const Row = ({ b, onCancelled }) => {
  const [busy, setBusy] = useState(false);
  const [arm, setArm] = useState(false);
  const cancel = async () => {
    setBusy(true);
    try {
      const { data } = await authApi.post(`/admin/bookings/${b.id}/cancel`);
      onCancelled(data);
      toast.success(`Cancelled — ${b.name} has been emailed.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
      setArm(false);
    }
  };
  return (
    <li className="grid gap-3 border-t border-white/10 py-5 md:grid-cols-12 md:items-center" data-testid={`admin-booking-${b.id}`}>
      <div className="md:col-span-3">
        <p className="font-mono text-xs tracking-[0.1em] text-white">{fmtLocal(b.slot_start)}</p>
        <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">UK time · 30 min</p>
      </div>
      <div className="md:col-span-3">
        <p className="text-sm text-white">{b.name}</p>
        <a href={`mailto:${b.email}`} className="text-xs text-slate-400 hover:text-gold">{b.email}</a>
      </div>
      <div className="md:col-span-3">
        <p className="text-sm text-slate-300">{b.company || "—"}</p>
        <p className="text-xs text-slate-500">{b.topic}</p>
      </div>
      <div className="flex items-center gap-3 md:col-span-3 md:justify-end">
        <StatusPill status={b.status} />
        {b.can_manage && !arm && (
          <button type="button" data-testid={`admin-cancel-${b.id}`} onClick={() => setArm(true)} className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-red-300">
            <XCircle className="h-3.5 w-3.5" /> Cancel
          </button>
        )}
        {arm && (
          <span className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em]">
            <button type="button" data-testid={`admin-cancel-confirm-${b.id}`} disabled={busy} onClick={cancel} className="text-red-300 hover:text-red-200">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Confirm"}
            </button>
            <button type="button" onClick={() => setArm(false)} className="text-slate-500 hover:text-white">Keep</button>
          </span>
        )}
      </div>
      {b.notes && <p className="text-xs leading-relaxed text-slate-400 md:col-span-12">“{b.notes}”</p>}
    </li>
  );
};

export const BookingsTable = ({ bookings, onUpdate }) => {
  if (bookings.length === 0) {
    return <p className="border-t border-white/10 py-10 text-sm text-slate-500" data-testid="admin-bookings-empty">Nothing here yet.</p>;
  }
  return (
    <ul data-testid="admin-bookings-list">
      {bookings.map((b) => (
        <Row key={b.id} b={b} onCancelled={onUpdate} />
      ))}
    </ul>
  );
};
